// Editing a scenario's values on the scenario screen (TX-US-0025, TX-US-0026).
//
// The model's structure - its timeline points, payment terms and cost lines - comes from the API
// and is what the screen draws. The scenario holds only values for it, keyed by the structure's
// keys; a field with no value is null or absent, shown empty and calculated as nothing.
//
// Values are held as the API sends them: every number a string. A slider yields a string and the
// box beside it keeps one, so a figure goes back exactly as typed and no amount is rounded by
// passing through a float. Number() is for drawing the slider, never for what is sent.

// A copy of `values` with the value at `path` replaced. Only the containers on the path are
// copied; one missing on the way - a field the scenario has no value for yet - is created.
export function setIn(values, path, value) {
  if (path.length === 0) return value;
  const [head, ...rest] = path;
  const container = values ?? {};
  const copy = Array.isArray(container) ? [...container] : { ...container };
  copy[head] = setIn(container[head], rest, value);
  return copy;
}

// ISO dates, as the API sends them, read and written as UTC days.
const DAY_MS = 24 * 60 * 60 * 1000;
const daysBetween = (from, to) => Math.round((Date.parse(to) - Date.parse(from)) / DAY_MS);
export const addDays = (iso, days) =>
  new Date(Date.parse(iso) + days * DAY_MS).toISOString().slice(0, 10);

// Where a supplier PO starts from (TX-TSK-0131): a fixed date, the first buyer payment, or the
// start of a supplier PO listed before it, named by its position - `SUPPLY:0` is the first.
export const FIRST_PAYMENT = 'FIRST_PAYMENT';
const SUPPLY_PREFIX = 'SUPPLY:';
export const supplyAnchor = (index) => `${SUPPLY_PREFIX}${index}`;
const followed = (start) => (start?.startsWith(SUPPLY_PREFIX)
  ? Number(start.slice(SUPPLY_PREFIX.length)) : null);

// The choices of start for the supplier PO at `index`: each lists one more than the one before.
export const startChoices = (supply, index) => [
  { key: FIXED_DATE, label: 'On a fixed date' },
  { key: FIRST_PAYMENT, label: 'From First payment from Buyer' },
  ...supply.slice(0, index).map((o, i) => ({ key: supplyAnchor(i), label: `From ${o.label} start` })),
];

// A new supplier PO: a copy of the last one, named next in line and started a month after it. The
// first is named and otherwise empty.
const NEXT_PO_GAP_DAYS = 30;

export function addSupplyOrder(values) {
  const supply = values.supply ?? [];
  const last = supply[supply.length - 1];
  const label = `Supplier PO ${supply.length + 1}`;
  if (!last) return { ...values, supply: [{ label }] };
  const start = {
    start_on_date: null,
    start_from: supplyAnchor(supply.length - 1),
    start_days: String(NEXT_PO_GAP_DAYS),
    start_offset_days: null,
  };
  return { ...values, supply: [...supply, { ...structuredClone(last), label, ...start }] };
}

// The supplier POs without the one at `index`. One that started from it is left on a fixed date,
// the day it started (`starts`, the calculated start of each PO); one that started from a PO after
// it follows the same PO, now one place earlier.
export function removeSupplyOrder(values, index, starts = []) {
  if (values.supply.length <= 1) return values;
  const supply = values.supply.flatMap((order, i) => {
    if (i === index) return [];
    const from = followed(order.start_from);
    if (from === index) {
      return [{ ...order, start_on_date: starts[i] ?? null, start_from: null, start_days: null }];
    }
    if (from !== null && from > index) return [{ ...order, start_from: supplyAnchor(from - 1) }];
    return [order];
  });
  return { ...values, supply };
}

// Where each kind of figure's slider runs. `percent` shows a fraction as a percentage; the value
// held is still the fraction. A value beyond the range stretches it rather than being clamped.
export const RANGES = {
  quantity: { min: 0, max: 50000, step: 100, unit: 'MT' },
  price: { min: 0, max: 3000, step: 5, unit: '$/MT' },
  commissionRate: { min: 0, max: 0.3, step: 0.005, percent: true },
  share: { min: 0, max: 1, step: 0.05, percent: true },
  delay: { min: -60, max: 180, step: 1, unit: 'days' },
  commissionDelay: { min: 0, max: 180, step: 1, unit: 'days' },
  point: { min: -60, max: 180, step: 1, unit: 'days' },
  startOffset: { min: -60, max: 365, step: 1, unit: 'days' },
  annualRate: { min: 0, max: 0.3, step: 0.005, percent: true },
  facility: { min: 0, max: 30000000, step: 250000, unit: '$' },
  PER_MT: { min: 0, max: 1000, step: 1, unit: '$/MT' },
  PCT_OF_PURCHASE: { min: 0, max: 0.2, step: 0.00025, percent: true },
  PER_UNIT: { min: 0, max: 10000, step: 10, unit: '$/unit' },
  PER_MT_PER_DAY: { min: 0, max: 5, step: 0.01, unit: '$/MT/day' },
  PER_MT_PER_DAY_FOR_DAYS: { min: 0, max: 30, step: 0.01, unit: '$/MT/day' },
  days: { min: 0, max: 180, step: 1, unit: 'days' },
  FIXED: { min: 0, max: 5000000, step: 1000, unit: '$' },
};

// A fraction shown as a percentage and read back, without the float noise 0.07 * 100 carries.
const PERCENT_DIGITS = 6;

export const toPercent = (fraction) => String(Number((Number(fraction) * 100).toFixed(PERCENT_DIGITS)));

export const fromPercent = (percent) =>
  String(Number((Number(percent) / 100).toFixed(PERCENT_DIGITS + 2)));

// The points a trigger or an anchor may name: a supplier PO's start, the buyer PO's start, and
// the model's timeline points.
export const ORDER = 'ORDER';
export const PO_START = 'PO_START';

export function pointLabel(structure, key) {
  if (key === ORDER) return 'Supplier PO start';
  if (key === PO_START) return 'Buyer PO start';
  return structure.points.find((p) => p.key === key)?.label ?? key;
}

// The points a payment term may be due from (TX-TSK-0128, TX-TSK-0129): the supplier PO's start
// and every timeline point, and for a buyer term the buyer PO's start as well. A supplier's term
// and a cost are paid on a supplier PO's own timeline, where the buyer PO's start is not.
export const dueFromChoices = (structure, { buyer = false } = {}) =>
  [...(buyer ? [PO_START] : []), ORDER, ...structure.points.map((p) => p.key)].map((key) => ({
    key,
    label: pointLabel(structure, key),
  }));

// The point a term is due from: the scenario's choice, or the model's.
export const dueFrom = (term, values) => values?.due_from || term.trigger;

// The cost lines in the model's order, the costs of one group gathered where its first stands: a
// group is one charge, due as one, so it is drawn as one.
export function costLines(structure) {
  const lines = [];
  for (const cost of structure.costs) {
    const line = cost.group && lines.find((l) => l.group === cost.group);
    if (line) line.costs.push(cost);
    else lines.push({ group: cost.group ?? null, costs: [cost] });
  }
  return lines;
}

// Timeline points (TX-US-0026). A point is on a fixed date, or `offset_days` from the point named
// by `after`; every supplier PO runs the timeline from its own start. A point with no anchor is
// shown as fixed - with no date, when it is empty. `FIXED_DATE` is the anchor choice for a fixed
// point; no point key is empty.
export const FIXED_DATE = '';

// The points `key` may be dated from: the supplier PO's start and every point except itself and
// the points dated, however indirectly, from it - choosing one of those would close a cycle.
export function timelineAnchors(structure, timeline, key) {
  const blocked = new Set([key]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const [other, point] of Object.entries(timeline ?? {})) {
      if (!blocked.has(other) && point?.after && blocked.has(point.after)) {
        blocked.add(other);
        grew = true;
      }
    }
  }
  return [ORDER, ...structure.points.map((p) => p.key)]
    .filter((k) => !blocked.has(k))
    .map((k) => ({ key: k, label: pointLabel(structure, k) }));
}

// `point` re-anchored without moving it: fixed takes the date it has, relative takes the offset
// that gives that date. `dates` maps point keys to their calculated ISO dates for one supplier PO
// (the first); a point with no date keeps what it has, or stays empty.
export function reanchor(point, key, anchor, dates) {
  const current = dates[key];
  if (anchor === FIXED_DATE) {
    return { after: null, on_date: current ?? point?.on_date ?? null, offset_days: null };
  }
  const offset = current && dates[anchor] ? String(daysBetween(dates[anchor], current))
    : point?.offset_days ?? null;
  return { after: anchor, on_date: null, offset_days: offset };
}

// A supplier PO starts on a fixed date, or `start_days` from the point `start_from`. One with no
// point is shown as fixed - with no date, when it is empty. Re-anchoring keeps the day it starts
// when that day and the new point's are known: `dates` are the calculated `starts` of the supplier
// POs and the `firstPayment`. Any start from the buyer PO's start, saved before, goes with it.
export const isFixedStart = (order) => order.start_from == null;

export function reanchorStart(order, index, anchor, { starts = [], firstPayment = null } = {}) {
  const current = starts[index] ?? null;
  if (anchor === FIXED_DATE) {
    return {
      ...order,
      start_on_date: current ?? order.start_on_date ?? null,
      start_from: null,
      start_days: null,
      start_offset_days: null,
    };
  }
  const from = anchor === FIRST_PAYMENT ? firstPayment : starts[followed(anchor)] ?? null;
  const days = current && from ? String(daysBetween(from, current)) : (order.start_days ?? '0');
  return { ...order, start_on_date: null, start_from: anchor, start_days: days, start_offset_days: null };
}

// The label of the point a supplier PO starts from.
export const startLabel = (supply, order) => {
  const from = followed(order.start_from);
  if (from !== null) return `${supply[from]?.label ?? 'Supplier PO'} start`;
  return 'First payment from Buyer';
};

// The buyer tranches' commission shares, as a percentage of the whole commission. A scenario is
// saved only when they make it up; an empty share counts as nothing. The API decides - this only
// spares a save it would refuse.
export function commissionTotal(structure, values) {
  const shares = structure.sale_terms.map((t) => values.sale?.terms?.[t.key]?.commission_share);
  return Number(toPercent(shares.reduce((sum, share) => sum + Number(share ?? 0), 0)));
}

export const COMMISSION_WHOLE = 100;

// Whether leaving a scenario must ask first: only with figures changed and not saved, only when the
// page really changes, and never when the screen itself leaves on purpose.
export const blocksLeaving = ({ changed, onPurpose, from, to }) =>
  changed && !onPurpose && from !== to;
